import { doc, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import { db } from '../firebase';
import { formatDate } from '../utils/helpers';
import { amountToFrench, formatNumber2 } from '../utils/finance';
import { findItemsTableInXml, modifyItemsTable, addEspeceRows, injectRowLoop } from '../utils/docxMerge';

// Génération de devis / factures à partir de modèles Word (.docx), même mécanisme que METAFLOW :
//  1. remplacement des balises {{...}} (docxtemplater) ;
//  2. clonage de la ligne d'article du tableau contenant « TOTAL » et « TVA » (6 cellules :
//     N°, Désignation, Unité, Qté, P.U., Montant), une ligne par article ;
//  3. paiement en espèces (facture) : lignes « Frais du timbre » (0,25 %) et « TOTAL » ajoutées après « TOTAL TTC ».
// Les modèles sont stockés en base64 dans Firestore (pas de stockage de fichiers).

export const TEMPLATE_MAX_BYTES = 600 * 1024; // base64 +33 % < 800 Ko (limite Firestore 1 Mio / doc)
export const TEMPLATE_TYPES = [
  { key: 'devis', label: 'Devis' },
  { key: 'facture', label: 'Facture' }
];
export const FRAIS_TIMBRE_RATE = 0.0025;

// Balises disponibles (noms METAFLOW + alias en français)
export const TEMPLATE_TAGS = [
  ['ref', 'Référence courte (D04 / F04)'], ['invoice_number', 'Numéro (04)'], ['invoice_year', 'Année (2026)'],
  ['numero', 'Numéro complet (04/2026)'], ['date', 'Date (jj/mm/aaaa)'], ['type', 'DEVIS ou FACTURE'],
  ['client_name', 'Nom du client'], ['client_ice', 'ICE / CIN du client'], ['client_adresse', 'Adresse du client'],
  ['project_description', 'Objet / description du projet'], ['dossier_ref', 'Référence du dossier'],
  ['total_ht', 'Total HT'], ['taux_tva', 'Taux de TVA (%)'], ['tva', 'Montant TVA'], ['total_ttc', 'Total TTC'],
  ['amount_words', 'Montant en lettres'], ['espece', 'Oui / Non (paiement en espèces)'],
  ['frais_timbre', 'Frais du timbre (espèces)'], ['total_final', 'Total à payer (TTC + timbre)'],
  ['notes', 'Notes / conditions'],
  ['cabinet_nom', 'Nom de la société'], ['architecte', "Nom de l'architecte"], ['adresse_cabinet', 'Siège social'],
  ['ice', 'ICE du cabinet'], ['if', 'Identifiant fiscal'], ['rc', 'RC'], ['patente', 'Patente'], ['rib', 'RIB'],
  ['banque', 'Banque'], ['email', 'Email'], ['telephone', 'Mobile / fixe'],
  ['articles', 'Tableau : lignes clonées automatiquement (tableau contenant TOTAL et TVA, en-tête N° / Désignation)']
];

const templateId = (cabinetId, type) => `${cabinetId}_${type}`;
const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const toBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(',')[1]);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});
const fromBase64 = (data) => Uint8Array.from(atob(data), (c) => c.charCodeAt(0));

// ---------- Service ----------
export class TemplateService {
  static async get(cabinetId, type) {
    try {
      const snap = await getDoc(doc(db, 'doc_templates', templateId(cabinetId, type)));
      return { success: true, template: snap.exists() ? { id: snap.id, ...snap.data() } : null };
    } catch (error) {
      return { success: false, error: error.message, template: null };
    }
  }

  static async getAll(cabinetId) {
    const out = {};
    for (const t of TEMPLATE_TYPES) out[t.key] = (await TemplateService.get(cabinetId, t.key)).template;
    return out;
  }

  static async save(cabinetId, type, file) {
    try {
      if (!/\.docx$/i.test(file.name)) return { success: false, error: 'Le modèle doit être un fichier .docx' };
      if (file.size > TEMPLATE_MAX_BYTES) return { success: false, error: `Fichier trop volumineux (max ${Math.round(TEMPLATE_MAX_BYTES / 1024)} Ko)` };
      const data = await toBase64(file);
      // Validation : ouvrable, balises compilables
      const zip = new PizZip(fromBase64(data));
      new Docxtemplater(zip, { paragraphLoop: true, linebreaks: true, delimiters: { start: '{{', end: '}}' } });
      const hasTable = !!findItemsTableInXml(zip.file('word/document.xml').asText());
      await setDoc(doc(db, 'doc_templates', templateId(cabinetId, type)), {
        cabinetId, type, name: file.name, size: file.size, data, hasItemsTable: hasTable, updatedAt: serverTimestamp()
      });
      return { success: true, hasItemsTable: hasTable };
    } catch (error) {
      console.error('Erreur modèle:', error);
      const detail = error?.properties?.errors?.map((e) => e.properties?.explanation).filter(Boolean).join(' ; ');
      return { success: false, error: detail ? `Modèle invalide : ${detail}` : error.message };
    }
  }

  static async remove(cabinetId, type) {
    try { await deleteDoc(doc(db, 'doc_templates', templateId(cabinetId, type))); return { success: true }; }
    catch (error) { return { success: false, error: error.message }; }
  }

  // Balises injectées (noms METAFLOW + alias)
  static buildData(facture, profile = {}) {
    const numero = facture.numero || '';
    const [num, year] = numero.split('/');
    const isDevis = facture.type === 'devis';
    const fraisTimbre = facture.fraisTimbre || 0;
    const totalFinal = facture.totalFinal || facture.montantTTC || 0;
    const clientIdent = (facture.clientIdentifiant || '').replace(/^(ICE|CIN)\s*/i, '');
    return {
      ref: `${isDevis ? 'D' : 'F'}${num || ''}`, invoice_number: num || '', invoice_year: year || '', numero,
      date: formatDate(facture.date), type: isDevis ? 'DEVIS' : 'FACTURE',
      client_name: facture.clientNom || '', client_nom: facture.clientNom || '',
      client_ice: clientIdent, client_identifiant: facture.clientIdentifiant || '', client_adresse: facture.clientAdresse || '',
      project_description: facture.objet || '', objet: facture.objet || '', dossier_ref: facture.refGlobal || '', notes: facture.notes || '',
      total_ht: formatNumber2(facture.montantHT || 0), montant_ht: formatNumber2(facture.montantHT || 0),
      taux_tva: String(facture.tauxTVA ?? 0), tva: formatNumber2(facture.montantTVA || 0), montant_tva: formatNumber2(facture.montantTVA || 0),
      total_ttc: formatNumber2(facture.montantTTC || 0), montant_ttc: formatNumber2(facture.montantTTC || 0),
      amount_words: `${amountToFrench(totalFinal)} `, montant_lettres: `${amountToFrench(totalFinal)} dirhams`,
      espece: facture.espece ? 'Oui' : 'Non', frais_timbre: fraisTimbre ? formatNumber2(fraisTimbre) : '',
      total_final: formatNumber2(totalFinal),
      cabinet_nom: profile.nomSociete || profile.nomArchitecte || '', architecte: profile.nomArchitecte || '',
      adresse_cabinet: profile.siegeSocial || '', ice: profile.ice || '', if: profile.identifiantFiscal || '', rc: profile.rc || '',
      patente: profile.patente || '', rib: profile.rib || '', banque: profile.banque || '', email: profile.email || '',
      telephone: [profile.mobile, profile.fixe].filter(Boolean).join(' / ')
    };
  }

  // Génère le .docx rempli et le télécharge
  static render(template, facture, profile) {
    const zip = new PizZip(fromBase64(template.data));
    const items = (facture.lignes || []).map((l, i) => {
      const q = parseFloat(l.quantite) || 0; const pu = parseFloat(l.prixUnitaire) || 0;
      return { num: i + 1, designation: l.designation || '', unite: l.unite || 'F', quantite: q, prix_unitaire: pu, montant: q * pu };
    });
    // 1. Ligne d'article à balises → boucle {{#lignes}} (modèles type FACTURE_EZ)
    const pre = injectRowLoop(zip.file('word/document.xml').asText());
    if (pre.injected) zip.file('word/document.xml', pre.xml);
    const docx = new Docxtemplater(zip, { paragraphLoop: true, linebreaks: true, delimiters: { start: '{{', end: '}}' } });
    docx.render({
      ...TemplateService.buildData(facture, profile),
      lignes: items.map((it) => ({ ...it, prix_unitaire: formatNumber2(it.prix_unitaire), montant: formatNumber2(it.montant) }))
    });
    const rendered = docx.getZip();
    let xml = rendered.file('word/document.xml').asText();
    if (findItemsTableInXml(xml)) {
      // 2. Sinon, clonage METAFLOW de la ligne modèle à 6 cellules
      if (!pre.injected) xml = modifyItemsTable(xml, items);
      if (facture.espece && facture.fraisTimbre) xml = addEspeceRows(xml, formatNumber2(facture.fraisTimbre), formatNumber2(facture.totalFinal || 0));
    }
    rendered.file('word/document.xml', xml);
    const blob = rendered.generate({ type: 'blob', mimeType: DOCX_MIME });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${facture.type === 'devis' ? 'Devis' : 'Facture'}_${(facture.numero || 'brouillon').replace('/', '-')}_${(facture.clientNom || 'client').replace(/[^\w-]+/g, '_')}.docx`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }
}
